import type { Delivery } from '@/api/types';
import { filterValidDeliveryIds, isUuid } from '@/utils/uuid';

/** Stable row key for delivery bulk selection — always the delivery UUID. */
export function deliveryRowKey(delivery: Delivery): string {
  return delivery.id;
}

/**
 * Resolve delivery UUIDs to retry from selected rows and selection keys.
 * Never returns subscription IDs, link IDs, row indices, or non-UUID placeholders.
 */
export function resolveBulkRetryDeliveryIds(
  deliveries: Delivery[],
  selectedKeys: Iterable<string>,
  isRetryable: (delivery: Delivery) => boolean,
): string[] {
  const selected = new Set(selectedKeys);
  const pageById = new Map<string, Delivery>();

  for (const delivery of deliveries) {
    if (isUuid(delivery.id)) {
      pageById.set(delivery.id, delivery);
    }
  }

  const ids = new Set<string>();

  for (const delivery of deliveries) {
    if (!isUuid(delivery.id) || !selected.has(delivery.id) || !isRetryable(delivery)) {
      continue;
    }
    ids.add(delivery.id);
  }

  for (const key of Array.from(selected)) {
    if (!isUuid(key) || ids.has(key)) {
      continue;
    }
    const row = pageById.get(key);
    if (!row || isRetryable(row)) {
      ids.add(key);
    }
  }

  return filterValidDeliveryIds(Array.from(ids));
}
